"use client";

import { cn } from "@/utils/cn";
import { forwardRef } from "react";
import {
	Input as AriaInput,
	InputProps as AriaInputProps,
	NumberField as AriaNumberField,
	NumberFieldProps as AriaNumberFieldProps,
	ValidationResult as AriaValidationResult,
	composeRenderProps,
	Label,
	Text,
} from "react-aria-components";

import currencify from "@/utils/currencify";
import { FieldError, FieldGroup } from "./field";

interface CurrencyFieldProps extends Omit<AriaNumberFieldProps, "formatOptions"> {
	label?: string;
	description?: string;
	errorMessage?: string | ((validation: AriaValidationResult) => string);
	currency?: string;
	locale?: string;
	showSymbol?: boolean;
	placeholder?: string;
	className?: string;
	showHelperText?: boolean;
	isDisabled?: boolean;
	onDirectInput?: (e: React.ChangeEvent<HTMLInputElement>) => void;
	currencifyOptions?: {
		abbreviated?: boolean;
		short?: boolean;
		numberWithCommas?: boolean;
		fullWords?: boolean;
		roundOff?: boolean;
		minimumFractionDigits?: number;
		maximumFractionDigits?: number;
		toFixedDigits?: boolean;
	};
}

const CurrencyFieldInput = forwardRef<HTMLInputElement, AriaInputProps>(({ className, ...props }, ref) => {
	return (
		<AriaInput
			ref={ref}
			className={composeRenderProps(className, className =>
				cn(
					"w-full min-w-0 flex-1 bg-background px-3 py-2 outline-0 placeholder:text-muted-foreground",
					"text-sm font-medium border-0 focus:border-0 focus:ring-0",
					className,
				),
			)}
			{...props}
		/>
	);
});
CurrencyFieldInput.displayName = "CurrencyFieldInput";

export const CurrencyField = ({
	label,
	errorMessage,
	currency: _currency = "INR",
	locale: _locale = "en-IN",
	showSymbol = true,
	placeholder = "",
	className,
	showHelperText = true,
	currencifyOptions = { abbreviated: true },
	isDisabled,
	...props
}: CurrencyFieldProps) => {
	// For currency fields, add direct input listener for real-time updates
	const handleDirectInput = (e: React.ChangeEvent<HTMLInputElement>) => {
		const rawValue = e.target.value.replace(/,/g, "");
		const inputValue = parseFloat(rawValue);
		if (!isNaN(inputValue)) {
			props.onChange?.(inputValue);
		}
	};

	// Calculate helper text directly
	const helperText = showHelperText && props.value ? currencify({ amount: props.value, ...currencifyOptions }) : "";

	return (
		<AriaNumberField
			className={composeRenderProps(className, className => cn("group flex flex-col gap-2", className))}
			formatOptions={{
				style: "decimal",
				minimumFractionDigits: 0,
				maximumFractionDigits: 0,
				useGrouping: true,
			}}
			{...props}
		>
			{label && <Label className={cn("text-title-medium")}>{label}</Label>}
			<FieldGroup className="relative" variant="ghost" isDisabled={isDisabled}>
				{showSymbol && (
					<div className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-medium text-muted-foreground z-30 pointer-events-none">
						₹
					</div>
				)}
				<CurrencyFieldInput
					placeholder={placeholder}
					onInput={handleDirectInput}
					className={cn(
						"h-10 w-full rounded-md border border-input bg-background py-2 text-sm ring-offset-background",
						"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
						"disabled:cursor-not-allowed disabled:opacity-50",
						showSymbol ? "pl-8 pr-3" : "px-3",
					)}
				/>
			</FieldGroup>
			{/* Helper text showing formatted currency */}
			{helperText && (
				<Text slot="description" className="text-sm text-muted-foreground">
					{helperText}
				</Text>
			)}
			<FieldError>{errorMessage}</FieldError>
		</AriaNumberField>
	);
};

export type { CurrencyFieldProps };
